import { Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';
import { useToggleFavorite, useCheckFavorited } from '@/hooks/useFavorites';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

interface FavoriteButtonProps {
  scoreId: string;
  size?: 'sm' | 'md';
  onToggle?: (isFavorited: boolean) => void;
}

export function FavoriteButton({ 
  scoreId, 
  size = 'md',
  onToggle 
}: FavoriteButtonProps) {
  const { user } = useAuth();
  const toggleMutation = useToggleFavorite();
  
  // Always use React Query as the single source of truth
  const { data: isFavorited, isLoading } = useCheckFavorited(user?.id, scoreId);

  const handleToggle = () => {
    if (!user) {
      toast.error('Please sign in to save favorites');
      return;
    }
    
    toggleMutation.mutate(
      { userId: user.id, scoreId, isFavorited: !!isFavorited },
      {
        onSuccess: () => {
          onToggle?.(!isFavorited);
        }
      }
    );
  };

  // Use the query result as the source of truth
  const showFavorited = isFavorited ?? false;
  const isDisabled = toggleMutation.isPending || isLoading;

  const iconSize = size === 'sm' ? 'h-4 w-4' : 'h-5 w-5';
  const buttonSize = size === 'sm' ? 'h-8 w-8 p-0' : 'h-9 w-9 p-0';

  return (
    <Button 
      variant="ghost" 
      size="icon"
      className={cn(buttonSize, 'flex-shrink-0', isLoading && 'opacity-50')}
      onClick={(e) => { 
        e.stopPropagation(); 
        handleToggle(); 
      }}
      disabled={isDisabled}
      title={showFavorited ? 'Remove from favorites' : 'Add to favorites'}
    >
      <Star 
        className={cn(
          iconSize,
          'transition-colors',
          showFavorited 
            ? 'fill-yellow-500 text-yellow-500' 
            : 'text-muted-foreground hover:text-yellow-500'
        )} 
      />
    </Button>
  );
}
